import { ref, watch, onMounted, onUnmounted, type Ref } from 'vue'

export interface SEOData {
  title?: string
  description?: string
  keywords?: string
  ogTitle?: string
  ogDescription?: string
  ogImage?: string
  ogType?: string
  ogUrl?: string
  twitterCard?: string
  twitterTitle?: string
  twitterDescription?: string
  twitterImage?: string
  canonical?: string
  noindex?: boolean
  nofollow?: boolean
}

const DEFAULT_TITLE = 'Rifas La Favorita - ¡Atrapa tu Suerte!'
const DEFAULT_DESCRIPTION = 'Participa en los mejores sorteos y gana premios increíbles con Rifas La Favorita. Sorteos seguros, transparentes y con premios en efectivo y vehículos.'
const DEFAULT_OG_IMAGE = 'https://media.lafavoritarifas.com/web/logo.png'
const SITE_URL = 'https://www.lafavoritarifas.com'

function updateMetaTag(name: string, content: string) {
  if (!content) return
  
  let tag = document.querySelector(`meta[name="${name}"]`) || 
            document.querySelector(`meta[property="${name}"]`)
  
  if (tag) {
    tag.setAttribute('content', content)
  } else {
    tag = document.createElement('meta')
    if (name.startsWith('og:') || name.startsWith('twitter:')) {
      tag.setAttribute('property', name)
    } else {
      tag.setAttribute('name', name)
    }
    tag.setAttribute('content', content)
    document.head.appendChild(tag)
  }
}

function updateLinkTag(rel: string, href: string) {
  if (!href) return
  
  let tag = document.querySelector(`link[rel="${rel}"]`)
  
  if (tag) {
    tag.setAttribute('href', href)
  } else {
    tag = document.createElement('link')
    tag.setAttribute('rel', rel)
    tag.setAttribute('href', href)
    document.head.appendChild(tag)
  }
}

function updateTitle(title: string) {
  document.title = title
}

function updateRobots(noindex: boolean, nofollow: boolean) {
  const robotsValue = [
    noindex ? 'noindex' : 'index',
    nofollow ? 'nofollow' : 'follow'
  ].join(', ')
  
  updateMetaTag('robots', robotsValue)
}

export function useSEO() {
  const previousData: Ref<SEOData> = ref({
    title: document.title,
    description: '',
    ogTitle: '',
    ogDescription: '',
    ogImage: '',
    ogType: '',
    ogUrl: '',
    twitterCard: '',
    twitterTitle: '',
    twitterDescription: '',
    twitterImage: '',
    canonical: '',
    noindex: false,
    nofollow: false
  })

  const updateSEO = (data: SEOData) => {
    const {
      title = DEFAULT_TITLE,
      description = DEFAULT_DESCRIPTION,
      keywords,
      ogTitle = title,
      ogDescription = description,
      ogImage = DEFAULT_OG_IMAGE,
      ogType = 'website',
      ogUrl = SITE_URL,
      twitterCard = 'summary_large_image',
      twitterTitle = ogTitle,
      twitterDescription = ogDescription,
      twitterImage = ogImage,
      canonical = ogUrl,
      noindex = false,
      nofollow = false
    } = data

    updateTitle(title)
    updateMetaTag('description', description)
    
    if (keywords) {
      updateMetaTag('keywords', keywords)
    }

    updateMetaTag('og:title', ogTitle)
    updateMetaTag('og:description', ogDescription)
    updateMetaTag('og:image', ogImage)
    updateMetaTag('og:type', ogType)
    updateMetaTag('og:url', ogUrl)
    updateMetaTag('og:site_name', 'Rifas La Favorita')

    updateMetaTag('twitter:card', twitterCard)
    updateMetaTag('twitter:title', twitterTitle)
    updateMetaTag('twitter:description', twitterDescription)
    updateMetaTag('twitter:image', twitterImage)
    updateMetaTag('twitter:site', '@rifaslafavorita')

    updateLinkTag('canonical', canonical)
    
    updateRobots(noindex, nofollow)
  }

  const updateRaffleSEO = (raffle: any) => {
    if (!raffle) return

    const title = `${raffle.title || 'Sorteo Exclusivo'} - Rifas La Favorita`
    const description = raffle.description || 
      `Participa en el sorteo de ${raffle.title || 'Rifas La Favorita'}. ` +
      `Tickets disponibles desde $${raffle.ticket_price || '4'}. ` +
      `¡Gana premios increíbles!`
    const ogImage = raffle.image_url || raffle.prize_image || DEFAULT_OG_IMAGE

    updateSEO({
      title,
      description,
      keywords: `${raffle.title}, sorteo, rifas venezuela, ${raffle.prize_name || 'premios'}, tickets online`,
      ogTitle: title,
      ogDescription: description,
      ogImage,
      ogType: 'product',
      ogUrl: `${SITE_URL}/sorteo/${raffle.id}`
    })
  }

  const setPageSEO = (page: string) => {
    const pageConfig: Record<string, SEOData> = {
      landing: {
        title: DEFAULT_TITLE,
        description: DEFAULT_DESCRIPTION,
        keywords: 'rifas venezuela, sorteos online, premios en efectivo, comprar tickets, rifas la favorita'
      },
      how_it_works: {
        title: 'Cómo Participar - Rifas La Favorita',
        description: 'Aprende cómo participar en nuestros sorteos. Elige tus tickets, realiza el pago y recibe tu certificado. ¡Fácil y seguro!',
        keywords: 'cómo participar, comprar tickets, rifas online, tutorial sorteos'
      },
      contact: {
        title: 'Contáctanos - Rifas La Favorita',
        description: '¿Tienes dudas? Contáctanos. Atención al cliente, soporte de pagos y ventas disponibles por teléfono, WhatsApp y correo electrónico.',
        keywords: 'contacto, soporte, ayuda rifas, atención al cliente'
      },
      winners: {
        title: 'Ganadores Anteriores - Rifas La Favorita',
        description: 'Conoce a los ganadores de nuestros sorteos anteriores. Transparencia total en todos nuestros resultados.',
        keywords: 'ganadores, sorteos anteriores, resultados rifas, premios entregados'
      },
      my_tickets: {
        title: 'Mis Tickets - Rifas La Favorita',
        description: 'Consulta y gestiona tus tickets de rifas. Verifica tu participación y descubre si eres un ganador.',
        keywords: 'mis tickets, consultar tickets, ver rifas, participación'
      },
      terms: {
        title: 'Términos y Condiciones - Rifas La Favorita',
        description: 'Términos y condiciones oficiales de Rifas La Favorita. Reglas, políticas de devolución y derechos de los participantes.',
        keywords: 'términos y condiciones, reglas rifas, políticas, condiciones de uso'
      },
      privacy: {
        title: 'Política de Privacidad - Rifas La Favorita',
        description: 'Política de privacidad de Rifas La Favorita. Cómo protegemos tus datos personales y transacciones.',
        keywords: 'privacidad, protección de datos, política de privacidad'
      }
    }

    updateSEO(pageConfig[page] || {})
  }

  const restorePreviousSEO = () => {
    updateSEO(previousData.value)
  }

  return {
    updateSEO,
    updateRaffleSEO,
    setPageSEO,
    restorePreviousSEO
  }
}
